import { useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  CarProfile,
  MagnifyingGlass,
  MapPin,
  Phone,
  Trash,
  UploadSimple,
  WhatsappLogo,
} from "@phosphor-icons/react";
import parkingData from "../parking/towerCparking.json";

const CACHE_KEY = "sp.tower-c-parking.v1";
const INDIA_PHONE = /^\d{10}$/;
const PLATE_PATTERN = /[A-Z]{2}[0-9O]{1,2}[A-Z]{1,3}[0-9O]{2,4}/g;

function normalizeVehicle(value = "") {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function normalizeSearch(value = "") {
  return String(value).toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function cleanPlateCandidate(value = "") {
  return normalizeVehicle(value).replace(/O/g, "0");
}

function plateMatchKeys(value = "") {
  const base = cleanPlateCandidate(value);
  const keys = new Set([base]);
  const match = base.match(/^([A-Z]{2})([0-9]{1,2})([A-Z]{1,3})([0-9]{2,4})$/);
  if (match) {
    const [, state, rto, series, serial] = match;
    const rtoNumber = String(Number(rto));
    if (rtoNumber !== "NaN") {
      keys.add(`${state}${rtoNumber}${series}${serial}`);
      keys.add(`${state}${rtoNumber.padStart(2, "0")}${series}${serial}`);
    }
    if (serial.length < 4) keys.add(`${state}${rto.padStart(2, "0")}${series}${serial.padStart(4, "0")}`);
  }
  return [...keys].filter(Boolean);
}

function ocrPlateCandidates(text = "") {
  const rawLines = String(text)
    .split(/\n+/)
    .map((line) => cleanPlateCandidate(line))
    .filter(Boolean);
  const tokens = String(text)
    .split(/[^A-Za-z0-9]+/)
    .map((token) => cleanPlateCandidate(token))
    .filter(Boolean);
  const candidates = new Set([cleanPlateCandidate(text), ...rawLines, ...tokens]);

  for (let i = 0; i < rawLines.length; i += 1) {
    for (let j = i + 1; j < Math.min(rawLines.length, i + 4); j += 1) {
      candidates.add(`${rawLines[i]}${rawLines[j]}`);
    }
  }

  for (let i = 0; i < tokens.length; i += 1) {
    let joined = "";
    for (let j = i; j < Math.min(tokens.length, i + 4); j += 1) {
      joined += tokens[j];
      candidates.add(joined);
    }
  }

  return [...candidates]
    .flatMap((candidate) => [candidate, ...(candidate.match(PLATE_PATTERN) || [])])
    .filter((candidate) => candidate.length >= 7 && candidate.length <= 12);
}

function normalizePhone(value = "") {
  const digits = String(value).replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
}

function formatPhone(value = "") {
  const digits = normalizePhone(value);
  if (digits.length !== 10) return value || "—";
  return `${digits.slice(0, 5)} ${digits.slice(5)}`;
}

function flatSortValue(flatNumber = "") {
  const text = String(flatNumber);
  const groups = text.match(/\d+/g) || [];
  if (groups.length > 1) return Number(groups[groups.length - 1]);
  const digits = groups[0];
  if (!digits) return Number.MAX_SAFE_INTEGER;
  // Handles source entries like C21704 where C2 is a tower/sub-wing marker
  // and 1704 is the actual flat number.
  if (digits.length > 4 && ["1", "2"].includes(digits[0])) return Number(digits.slice(1));
  return Number(digits);
}

function compareByFlat(a, b) {
  return (
    flatSortValue(a.resident?.flat_number) - flatSortValue(b.resident?.flat_number) ||
    String(a.resident?.flat_number || "").localeCompare(String(b.resident?.flat_number || "")) ||
    String(a.resident?.resident_name || "").localeCompare(String(b.resident?.resident_name || ""))
  );
}

function vehicleLabel(vehicle) {
  return [vehicle.number, vehicleTypeLabel(vehicle.type), vehicle.slot ? `Slot ${vehicle.slot}` : ""].filter(Boolean).join(" · ");
}

function vehicleTypeLabel(type = "") {
  if (type === "two_wheeler") return "2W";
  if (type === "four_wheeler") return "4W";
  return type || "Vehicle";
}

function istStamp() {
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  }).format(new Date());
}

function buildIndex(data) {
  return (data?.residents || []).flatMap((resident) =>
    (resident.vehicles || []).map((vehicle) => {
      const vehicleKey = normalizeVehicle(vehicle.normalized_number || vehicle.number);
      const nameKey = normalizeSearch(resident.resident_name);
      const flatKey = normalizeSearch(`${resident.flat_number || ""} ${resident.normalized_flat_number || ""}`);
      return {
        resident,
        vehicle,
        key: vehicleKey,
        matchKeys: plateMatchKeys(vehicleKey),
        searchKey: `${plateMatchKeys(vehicleKey).join(" ")} ${nameKey} ${flatKey}`,
      };
    }),
  );
}

function detectVehicleNumberFromText(text = "", index = []) {
  const candidates = ocrPlateCandidates(text);
  const exactKnown = index.find((row) => row.matchKeys?.some((key) => candidates.some((candidate) => candidate.includes(key))));
  if (exactKnown) return exactKnown.key;

  for (const candidate of candidates) {
    const candidateKeys = plateMatchKeys(candidate);
    const known = index.find((row) => row.matchKeys?.some((key) => candidateKeys.includes(key)));
    if (known) return known.key;
  }
  return cleanPlateCandidate(candidates[0] || "");
}

async function watermarkImage(file, vehicleNumber) {
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = URL.createObjectURL(file);
  });

  const maxSide = 1600;
  const ratio = Math.min(1, maxSide / Math.max(img.width, img.height));
  const width = Math.round(img.width * ratio);
  const height = Math.round(img.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  const stamp = `Tower C • ${vehicleNumber || "Vehicle"} • ${istStamp()} IST`;
  const pad = Math.max(18, Math.round(width * 0.018));
  const barHeight = Math.max(58, Math.round(width * 0.06));
  ctx.fillStyle = "rgba(5,5,5,0.72)";
  ctx.fillRect(0, height - barHeight, width, barHeight);
  ctx.fillStyle = "#ffffff";
  ctx.font = `700 ${Math.max(18, Math.round(width * 0.025))}px system-ui, sans-serif`;
  ctx.fillText(stamp, pad, height - Math.round(barHeight * 0.36));

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.86);
  });
}

function phoneActions(phone, whatsAppText) {
  const n = normalizePhone(phone.normalized_number || phone.number);
  if (!INDIA_PHONE.test(n)) return null;
  return (
    <div className="flex flex-wrap gap-2">
      <a className="btn-ghost !rounded-xl !px-3 !py-2 text-xs" href={`tel:+91${n}`}>
        <Phone size={15} weight="bold" /> Call
      </a>
      <a
        className="btn-lime !rounded-xl !px-3 !py-2 text-xs"
        href={`https://wa.me/91${n}?text=${encodeURIComponent(whatsAppText)}`}
        target="_blank"
        rel="noreferrer"
      >
        <WhatsappLogo size={15} weight="bold" /> WhatsApp
      </a>
    </div>
  );
}

export default function ParkingLookup() {
  const [query, setQuery] = useState("");
  const [loadedAt, setLoadedAt] = useState("");
  const [selected, setSelected] = useState(null);
  const [showMulti4W, setShowMulti4W] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerBusy, setScannerBusy] = useState(false);
  const [scannerStatus, setScannerStatus] = useState("");
  const [scannerError, setScannerError] = useState("");
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(parkingData));
      setLoadedAt(istStamp());
    } catch {
      setLoadedAt("Loaded in this browser");
    }
    fetch("/api/parking/photo", { method: "DELETE" }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!scannerOpen) return undefined;
    let cancelled = false;
    async function startScannerCamera() {
      setScannerError("");
      setScannerStatus("Starting camera…");
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Camera is not available in this browser.");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setScannerStatus("Point the camera at the vehicle number and tap scan.");
      } catch (err) {
        setScannerError(err.message || "Unable to start camera.");
        setScannerStatus("");
      }
    }
    startScannerCamera();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [scannerOpen]);

  const index = useMemo(() => buildIndex(parkingData), []);
  const vehicleCounts = useMemo(() => {
    return index.reduce(
      (acc, row) => {
        if (row.vehicle.type === "two_wheeler") acc.two += 1;
        else if (row.vehicle.type === "four_wheeler") acc.four += 1;
        else acc.other += 1;
        return acc;
      },
      { two: 0, four: 0, other: 0 },
    );
  }, [index]);
  const multiFourWheelers = useMemo(() => {
    return (parkingData?.residents || [])
      .map((resident) => ({
        resident,
        vehicles: (resident.vehicles || []).filter((vehicle) => vehicle.type === "four_wheeler"),
      }))
      .filter((row) => row.vehicles.length > 1)
      .sort(compareByFlat);
  }, []);
  const normalizedQuery = normalizeSearch(query);
  const normalizedQueryKeys = useMemo(() => [normalizedQuery, ...plateMatchKeys(normalizedQuery)], [normalizedQuery]);
  const matches = useMemo(() => {
    if (!normalizedQuery) return [];
    return index
      .filter((row) => normalizedQueryKeys.some((key) => key && row.searchKey.includes(key)))
      .sort(compareByFlat)
      .slice(0, 20);
  }, [index, normalizedQuery, normalizedQueryKeys]);

  const active = selected || matches[0] || null;
  const whatsAppText = active
    ? `Hello, this vehicle is parked in Tower C. Vehicle: ${active.vehicle.number}. Flat: ${active.resident.flat_number}. Photo: ${photoUrl || "not attached"}`
    : "Hello, sharing a Tower C parking vehicle update.";

  async function handlePhoto(file) {
    if (!file || !active) return;
    setPhotoBusy(true);
    setPhotoError("");
    setPhotoUrl("");
    try {
      const blob = await watermarkImage(file, active.vehicle.number);
      const form = new FormData();
      form.append("file", blob, `tower-c-${normalizeVehicle(active.vehicle.number)}.jpg`);
      const res = await fetch("/api/parking/photo", { method: "POST", body: form });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.url) throw new Error(body.detail || "Photo upload failed");
      setPhotoUrl(body.url);
    } catch (err) {
      setPhotoError(err.message || "Photo upload failed");
    } finally {
      setPhotoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function closeScanner() {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
    setScannerOpen(false);
    setScannerBusy(false);
  }

  async function captureAndScanVehicle() {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setScannerError("Camera preview is not ready yet.");
      return;
    }
    setScannerBusy(true);
    setScannerError("");
    setScannerStatus("Reading vehicle number…");
    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.filter = "contrast(1.35) saturate(0.25) grayscale(1)";
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      if (!blob) throw new Error("Could not capture scanner image.");

      const { recognize } = await import("tesseract.js");
      const result = await recognize(blob, "eng", {
        logger: (m) => {
          if (m.status === "recognizing text" && typeof m.progress === "number") {
            setScannerStatus(`Reading vehicle number… ${Math.round(m.progress * 100)}%`);
          }
        },
      });
      const detected = detectVehicleNumberFromText(result?.data?.text || "", index);
      if (!detected) {
        throw new Error("Vehicle number not detected. Try closer, straight, and with better light.");
      }
      setQuery(detected);
      setSelected(null);
      setPhotoUrl("");
      setPhotoError("");
      closeScanner();
    } catch (err) {
      setScannerError(err.message || "Unable to scan vehicle number.");
      setScannerStatus("Point clearly at the vehicle number and try again.");
      setScannerBusy(false);
    }
  }

  return (
    <main className="app-shell bg-aurora min-h-dvh px-4 py-5 text-white sm:px-6">
      <section className="mx-auto flex w-full max-w-6xl flex-col gap-5">
        <header className="card overflow-hidden p-5 shadow-2xl sm:p-7">
          <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="chip mb-3 border-neon-cyan/50 text-neon-cyan">
                <CarProfile size={14} weight="bold" /> Tower C Parking
              </div>
              <h1 className="font-display text-3xl font-black tracking-tight sm:text-5xl">
                Vehicle LookUp - Tower C
              </h1>
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              <button
                className={`btn ${showMulti4W ? "bg-neon-lime text-black shadow-glow-lime" : "bg-neon-cyan text-black shadow-glow-cyan"} !rounded-2xl !px-4 !py-3`}
                onClick={() => setShowMulti4W((v) => !v)}
              >
                Multi 4 W
              </button>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                  <div className="text-2xl font-black">{parkingData.metadata?.resident_record_count || 0}</div>
                  <div className="text-white/50">Residents</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                  <div className="text-2xl font-black">{parkingData.metadata?.vehicle_record_count || index.length}</div>
                  <div className="text-white/50">Vehicles ({`2W/${vehicleCounts.two}, 4W/${vehicleCounts.four}`})</div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <section className="card sticky top-3 z-10 p-3 shadow-2xl">
          <div className="flex items-center gap-3 rounded-3xl border-2 border-orange-500/70 bg-black/40 px-4 py-3 shadow-[0_0_0_1px_rgba(251,146,60,0.22),0_0_30px_rgba(251,146,60,0.16)] transition focus-within:border-orange-400 focus-within:shadow-[0_0_0_2px_rgba(251,146,60,0.35),0_0_36px_rgba(251,146,60,0.24)]">
            <MagnifyingGlass className="text-orange-300" size={24} weight="bold" />
            <input
              className="min-w-0 flex-1 bg-transparent text-[13px] font-black uppercase tracking-wide text-white outline-none placeholder:text-white/30 sm:text-sm"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelected(null);
                setPhotoUrl("");
                setPhotoError("");
              }}
              placeholder="Search vehicle, name or flat"
              autoCapitalize="characters"
              inputMode="text"
            />
            {query && (
              <button className="btn-ghost !rounded-xl !px-3 !py-2 text-xs" onClick={() => setQuery("")}>
                Clear
              </button>
            )}
            <button
              className="btn-primary !rounded-xl !px-3 !py-2 text-xs shadow-glow-cyan"
              onClick={() => {
                setScannerOpen(true);
                setScannerError("");
                setScannerStatus("");
              }}
            >
              <Camera size={16} weight="bold" /> Scan
            </button>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 px-2 text-xs text-white/45">
            <span>Offline data loaded: {loadedAt || "loading…"}</span>
            <span>{normalizedQuery ? `${matches.length} match${matches.length === 1 ? "" : "es"}` : "Type vehicle, name or flat"}</span>
          </div>
        </section>

        {normalizedQuery && matches.length === 0 && (
          <section className="card p-8 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-neon-pink/15 text-neon-pink">
              <MagnifyingGlass size={28} weight="bold" />
            </div>
            <h2 className="text-xl font-black">No vehicle found</h2>
            <p className="mt-2 text-white/55">Check the number and try again without spaces.</p>
          </section>
        )}

        {showMulti4W && (
          <section className="card overflow-hidden">
            <div className="flex flex-col gap-2 border-b border-white/10 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-black">Flats with multiple 4W vehicles</h2>
                <p className="text-sm text-white/50">Sorted by ascending flat number.</p>
              </div>
              <div className="chip text-neon-lime">{multiFourWheelers.length} flats</div>
            </div>
            <div className="divide-y divide-white/10">
              {multiFourWheelers.map((row) => (
                <button
                  key={row.resident.id}
                  className="grid w-full gap-3 p-4 text-left transition hover:bg-white/[0.04] sm:grid-cols-[110px_1fr_1.4fr] sm:items-center"
                  onClick={() => {
                    const vehicle = row.vehicles[0];
                    if (!vehicle) return;
                    setQuery(row.resident.flat_number || vehicle.number);
                    setSelected({
                      resident: row.resident,
                      vehicle,
                      key: normalizeVehicle(vehicle.normalized_number || vehicle.number),
                      searchKey: "",
                    });
                    setPhotoUrl("");
                    setPhotoError("");
                  }}
                >
                  <div className="rounded-2xl bg-neon-lime px-3 py-2 text-center text-lg font-black text-black">
                    {row.resident.flat_number}
                  </div>
                  <div>
                    <div className="font-black">{row.resident.resident_name}</div>
                    <div className="text-sm text-white/45">{row.vehicles.length} four wheelers</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {row.vehicles.map((vehicle) => (
                      <span key={vehicle.number} className="chip">
                        {vehicle.number}{vehicle.slot ? ` · Slot ${vehicle.slot}` : ""}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
              {multiFourWheelers.length === 0 && (
                <div className="p-6 text-sm text-white/55">No flat currently has more than one 4W entry.</div>
              )}
            </div>
          </section>
        )}

        {matches.length > 0 && (
          <div className="grid gap-4 lg:grid-cols-[1fr_1.05fr]">
            <section className="card overflow-hidden">
              <div className="border-b border-white/10 p-4">
                <h2 className="text-lg font-black">Search results</h2>
                <p className="text-sm text-white/50">Tap a row to prepare Call, WhatsApp and photo actions.</p>
              </div>
              <div className="divide-y divide-white/10">
                {matches.map((row) => {
                  const isActive = active?.key === row.key;
                  return (
                    <button
                      key={`${row.resident.id}-${row.vehicle.number}`}
                      className={`w-full p-4 text-left transition ${isActive ? "bg-neon-cyan/10" : "hover:bg-white/[0.04]"}`}
                      onClick={() => {
                        setSelected(row);
                        setPhotoUrl("");
                        setPhotoError("");
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xl font-black">{row.vehicle.number}</div>
                          <div className="mt-1 text-sm text-white/55">{row.resident.resident_name}</div>
                        </div>
                        <div className="rounded-2xl bg-neon-lime px-3 py-1 text-xs font-black text-black">
                          {row.resident.flat_number}
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-white/50">
                        <span className="chip">{vehicleTypeLabel(row.vehicle.type)}</span>
                        <span className="chip"><MapPin size={13} /> {row.vehicle.slot || "Slot not mapped"}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {active && (
              <section className="card overflow-hidden">
                <div className="border-b border-white/10 p-5">
                  <div className="chip mb-3 text-neon-lime">Owner details</div>
                  <h2 className="font-display text-3xl font-black">{active.resident.resident_name}</h2>
                  <p className="mt-1 text-white/55">
                    Flat {active.resident.flat_number} · Tower C · {vehicleLabel(active.vehicle)}
                  </p>
                </div>

                <div className="grid gap-4 p-5">
                  <div className="overflow-hidden rounded-3xl border border-white/10">
                    <table className="w-full text-left text-sm">
                      <tbody className="divide-y divide-white/10">
                        <tr>
                          <th className="w-36 bg-white/[0.04] p-4 text-white/50">Tower</th>
                          <td className="p-4 font-bold">C</td>
                        </tr>
                        <tr>
                          <th className="bg-white/[0.04] p-4 text-white/50">Flat</th>
                          <td className="p-4 font-bold">{active.resident.flat_number}</td>
                        </tr>
                        <tr>
                          <th className="bg-white/[0.04] p-4 text-white/50">Vehicle</th>
                          <td className="p-4 font-bold">{active.vehicle.number}</td>
                        </tr>
                        <tr>
                          <th className="bg-white/[0.04] p-4 text-white/50">Parking slot</th>
                          <td className="p-4 font-bold">{active.vehicle.slot || "—"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="grid gap-3">
                    {(active.resident.phones || []).map((phone, idx) => (
                      <div key={`${phone.number}-${idx}`} className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <div>
                            <div className="text-xs uppercase tracking-[0.22em] text-white/40">{phone.type || "Contact"}</div>
                            <div className="text-lg font-black">{formatPhone(phone.number)}</div>
                          </div>
                        </div>
                        {phoneActions(phone, whatsAppText)}
                      </div>
                    ))}
                  </div>

                  <div className="rounded-3xl border border-neon-cyan/30 bg-neon-cyan/10 p-4">
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-black">Attach vehicle photo</h3>
                        <p className="mt-1 text-sm text-white/55">
                          Photo is watermarked with Tower C, vehicle number and IST time. Server keeps it temporarily.
                        </p>
                      </div>
                      {photoUrl && (
                        <button className="btn-ghost !rounded-xl !px-3 !py-2 text-xs" onClick={() => setPhotoUrl("")}>
                          <Trash size={15} /> Clear
                        </button>
                      )}
                    </div>
                    <input
                      ref={fileRef}
                      className="hidden"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handlePhoto(e.target.files?.[0])}
                    />
                    <button className="btn-primary w-full" disabled={photoBusy} onClick={() => fileRef.current?.click()}>
                      {photoBusy ? <UploadSimple className="animate-pulse" size={18} weight="bold" /> : <Camera size={18} weight="bold" />}
                      {photoBusy ? "Uploading watermarked photo…" : "Click / upload photo"}
                    </button>
                    {photoError && <p className="mt-3 text-sm font-bold text-neon-pink">{photoError}</p>}
                    {photoUrl && (
                      <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-3">
                        <img src={photoUrl} alt="Watermarked vehicle" className="max-h-56 w-full rounded-xl object-cover" />
                        <p className="mt-2 break-all text-xs text-white/45">{photoUrl}</p>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}
          </div>
        )}

        {scannerOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-md">
            <div className="card w-full max-w-3xl overflow-hidden shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-white/10 p-5">
                <div>
                  <div className="chip mb-2 border-orange-400/50 text-orange-300">Vehicle scanner</div>
                  <h2 className="text-2xl font-black">Scan vehicle number</h2>
                  <p className="mt-1 text-sm text-white/55">Point the camera at the vehicle number, then tap scan. The photo is processed only in memory.</p>
                </div>
                <button className="btn-ghost !h-11 !w-11 !rounded-full !p-0 text-xl" onClick={closeScanner} aria-label="Close scanner">
                  ×
                </button>
              </div>

              <div className="p-4">
                <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-black">
                  <video ref={videoRef} className="aspect-video w-full object-cover" playsInline muted />
                </div>

                {(scannerStatus || scannerError) && (
                  <div className={`mt-3 rounded-2xl border p-3 text-sm font-bold ${scannerError ? "border-neon-pink/40 bg-neon-pink/10 text-neon-pink" : "border-orange-400/30 bg-orange-400/10 text-orange-200"}`}>
                    {scannerError || scannerStatus}
                  </div>
                )}

                <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
                  <button className="btn-primary w-full" disabled={scannerBusy || !!scannerError?.startsWith("Camera is not available")} onClick={captureAndScanVehicle}>
                    <Camera size={18} weight="bold" />
                    {scannerBusy ? "Scanning…" : "Scan vehicle number"}
                  </button>
                  <button className="btn-ghost" disabled={scannerBusy} onClick={closeScanner}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
